import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  View,
  StyleSheet,
  StatusBar,
} from 'react-native';
import { useNavigation, useRoute, useFocusEffect } from '@react-navigation/native';
import { useAppSelector, useAppDispatch } from '@/store';
import { refreshUser } from '@/store/slices/authSlice';
import { ResidentMobileHeaderWithMenu } from '@/components/residents/mobile/ResidentMobileHeaderWithMenu';
import { MobileScreenLayout } from '@/components/residents/mobile/MobileScreenLayout';
import { SettingsNavHeader, SettingsNavOption } from '@/components/common/residents/mobile/SettingsNavHeader';
import { useResidentScreen } from '@/hooks/residents/useResidentScreen';
import { ACCOUNT_COLORS } from '@/constants/residents/mobile/accountColors';

import { MobileAccountInfoSection } from './sections/MobileAccountInfoSection';
import { MobileResetPasswordSection } from './sections/MobileResetPasswordSection';
import { MobilePaymentMethodsSection } from './sections/MobilePaymentMethodsSection';
import { MobileAutopaySection } from './sections/MobileAutopaySection';
import { MobileNotificationSettingsSection } from './sections/MobileNotificationSettingsSection';

export type AccountSectionKey = 'accountInfo' | 'paymentMethods' | 'autopay' | 'notificationSettings' | 'resetPassword';

/**
 * Mobile Account Screen
 * Main account settings screen for mobile with collapsible settings navigation
 * Based on Figma designs for Account Settings
 */
export const MobileAccountScreen: React.FC = () => {
  const navigation = useNavigation<any>();
  const route = useRoute<any>();
  const dispatch = useAppDispatch();
  
  const { user } = useAppSelector((state) => state.auth);
  
  // Hide payment-related sections for Non-Leasing Occupants
  const shouldShowPayments = user?.customerType !== 'non-leasing occupant';
  
  // Active section state
  const [activeSection, setActiveSection] = useState<AccountSectionKey>('accountInfo');

  // Use resident screen hook for navigation handling
  const {
    handleNavigate,
    handleLogoPress,
    handleSignOut,
    handleNotificationPress,
  } = useResidentScreen('MobileAccount', navigation);

  // Handle initial section from route params
  useEffect(() => {
    const sectionParam = route?.params?.section as AccountSectionKey | undefined;
    if (sectionParam) {
      setActiveSection(sectionParam);
    }
  }, [route?.params?.section]);

  // Refresh user data when screen gains focus
  useFocusEffect(
    useCallback(() => {
      dispatch(refreshUser());
    }, [dispatch])
  );

  // Build navigation items based on user type
  const navOptions: SettingsNavOption<AccountSectionKey>[] = useMemo(() => {
    const items: SettingsNavOption<AccountSectionKey>[] = [
      {
        key: 'accountInfo',
        title: 'Account Information',
        description: 'Name and email',
      },
    ];

    if (shouldShowPayments) {
      items.push(
        {
          key: 'paymentMethods',
          title: 'Payment Method',
          description: 'View, add, or remove',
        },
        {
          key: 'autopay',
          title: 'Autopay',
          description: 'View, add, or remove',
        }
      );
    }

    items.push(
      {
        key: 'notificationSettings',
        title: 'Notification settings',
        description: 'Stay in the loop',
      },
      {
        key: 'resetPassword',
        title: 'Reset Password',
        description: 'Secure your account',
      }
    );

    return items;
  }, [shouldShowPayments]);

  // Handle section selection - now renders inline instead of navigating
  const handleSectionSelect = useCallback((key: AccountSectionKey) => {
    setActiveSection(key);
  }, []);

  // Handle account deleted
  const handleAccountDeleted = useCallback(() => {
    handleSignOut();
  }, [handleSignOut]);

  // Render the active section content
  const renderContent = () => {
    switch (activeSection) {
      case 'accountInfo':
        return <MobileAccountInfoSection onAccountDeleted={handleAccountDeleted} />;
      case 'paymentMethods':
        return <MobilePaymentMethodsSection />;
      case 'autopay':
        return <MobileAutopaySection />;
      case 'notificationSettings':
        return <MobileNotificationSettingsSection />;
      case 'resetPassword':
        return <MobileResetPasswordSection />;
      default:
        return <MobileAccountInfoSection onAccountDeleted={handleAccountDeleted} />;
    }
  };

  return (
    <View style={styles.container}>
      <StatusBar barStyle="light-content" />
      
      {/* Header with Navigation Menu */}
      <ResidentMobileHeaderWithMenu
        onLogoPress={handleLogoPress}
        onSignOut={handleSignOut}
        onNavigate={handleNavigate}
        onNotificationPress={handleNotificationPress}
        currentPath="/account-settings"
        currentTitle="Account"
        showNotification={true}
      />

      <MobileScreenLayout
        backgroundColor={ACCOUNT_COLORS.background}
        scrollable={false}
        lightStatusBar={false}
      >
        {/* Settings Navigation */}
        <SettingsNavHeader
          options={navOptions}
          selectedKey={activeSection}
          onSelect={handleSectionSelect}
          activeColor={ACCOUNT_COLORS.navActiveBackground}
          cardBackgroundColor={ACCOUNT_COLORS.contentBackground}
          textPrimaryColor={ACCOUNT_COLORS.textPrimary}
          textMutedColor={ACCOUNT_COLORS.textMuted}
        />

        {/* Content Section */}
        <View style={styles.contentSection}>
          {renderContent()}
        </View>
      </MobileScreenLayout>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: ACCOUNT_COLORS.contentBackground,
  },
  contentSection: {
    flex: 1,
    marginHorizontal: 20,
    marginTop: 24,
    marginBottom: 40,
  },
});

export default MobileAccountScreen;
